import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Select from '../../components/ui/Select';
import { serviceTypeService } from '../../services/serviceTypeService';
import { dealershipPricingService } from '../../services/dealershipPricingService';
import { carHouseService } from '../../services/carHouseService';

const ServiceTypesManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Data states
  const [dealerships, setDealerships] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [dealershipPricing, setDealershipPricing] = useState([]);
  
  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDealership, setSelectedDealership] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('dealership'); // 'dealership' or 'global'

  // Modal states
  const [isServiceTypeModalOpen, setIsServiceTypeModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState(serviceTypeService.getDefaultServiceTypeData());
     const [pricingFormData, setPricingFormData] = useState({
     price: 0,
     is_active: true,
     service_name: ''
   });
  const [formErrors, setFormErrors] = useState({});
  const [pricingFormErrors, setPricingFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load data when dealership selection changes
  useEffect(() => {
    if (selectedDealership) {
      loadDealershipPricing();
    }
  }, [selectedDealership]);

  const loadInitialData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load dealerships
      const { data: dealershipsData, error: dealershipsError } = await carHouseService.getAllCarHouses();
      if (dealershipsError) throw dealershipsError;
      
      // Load service types
      const { data: serviceTypesData, error: serviceTypesError } = await serviceTypeService.getAllServiceTypes();
      if (serviceTypesError) throw serviceTypesError;
      
      // Load global dealership pricing
      const { data: pricingData, error: pricingError } = await dealershipPricingService.getAllDealershipPricing();
      if (pricingError) throw pricingError;

      setDealerships(dealershipsData || []);
      setServiceTypes(serviceTypesData || []);
      setDealershipPricing(pricingData || []);
      
      // Set first dealership as default selection
      if (dealershipsData && dealershipsData.length > 0) {
        setSelectedDealership(dealershipsData[0]);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDealershipPricing = async () => {
    if (!selectedDealership) return;
    
    try {
      const { data, error } = await dealershipPricingService.getDealershipPricing(selectedDealership.id);
      if (error) throw error;
      
      setDealershipPricing(data || []);
    } catch (err) {
      console.error('Error loading dealership pricing:', err);
      setError(err.message);
    }
  };

  // Filter data based on current selection and search
  const filteredData = useMemo(() => {
    let data = viewMode === 'dealership' && selectedDealership 
      ? dealershipPricing.filter(item => item.dealership_id === selectedDealership.id)
      : serviceTypes;

    // Apply search filter
    if (searchTerm) {
      const searchFields = viewMode === 'dealership' 
        ? ['dealership_name', 'service_type_display_name', 'service_type_name']
        : ['name', 'display_name', 'description'];
      
      data = data.filter(item => 
        searchFields.some(field => 
          item[field]?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return data;
  }, [viewMode, selectedDealership, dealershipPricing, serviceTypes, searchTerm]);



  // Service Type Modal handlers
  const handleCreateServiceType = () => {
    setEditingServiceType(null);
    setFormData(serviceTypeService.getDefaultServiceTypeData());
    setFormErrors({});
    setIsServiceTypeModalOpen(true);
  };

     const handleEditServiceType = (serviceType) => {
     setEditingServiceType(serviceType);
          setFormData({
        name: serviceType.name,
        display_name: serviceType.display_name,
        description: serviceType.description || '',
        price: serviceType.price || 0.00
      });
    setFormErrors({});
    setIsServiceTypeModalOpen(true);
  };

  const handleSaveServiceType = async () => {
    
    // Custom validation for dealership view (where name is auto-generated)
    let validation;
    if (viewMode === 'dealership' && !editingServiceType) {
      // For new services in dealership view, only validate display_name and price
      const errors = {};
      if (!formData?.display_name?.trim()) {
        errors.display_name = 'Service Name ist erforderlich';
      }
      if (!formData?.price || formData?.price <= 0) {
        errors.price = 'Preis muss größer als 0 sein';
      }
      validation = {
        isValid: Object.keys(errors).length === 0,
        errors
      };
    } else {
      // Use standard validation for editing or global view
      validation = serviceTypeService.validateServiceTypeData(formData);
    }
    
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setIsSaving(true);
    try {
      let newServiceType;
      
      if (editingServiceType) {
        const { error } = await serviceTypeService.updateServiceType(editingServiceType.id, formData);
        if (error) throw error;
      } else {
        
        // For dealership view, we need to generate a technical name and set defaults
        if (viewMode === 'dealership') {
          if (!selectedDealership) {
            throw new Error('Kein Autohaus ausgewählt. Bitte wählen Sie zuerst ein Autohaus aus.');
          }
          
          // Generate a technical name from the display name
          const technicalName = formData.display_name
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');
          
          
          const serviceData = {
            ...formData,
            name: technicalName
          };
          
          const { data, error } = await serviceTypeService.createServiceType(serviceData);
          if (error) throw error;
          newServiceType = data;
        } else {
          const { data, error } = await serviceTypeService.createServiceType(formData);
          if (error) throw error;
          newServiceType = data;
        }
        
        // If we're in dealership view and have a selected dealership, also create pricing
        if (viewMode === 'dealership' && selectedDealership && newServiceType) {
          try {
            await dealershipPricingService.upsertDealershipPricing(
              selectedDealership.id,
              newServiceType.id,
              {
                price: formData.price,
                is_active: true
              }
            );
          } catch (pricingError) {
            // Don't fail the entire operation if pricing fails
          }
        }
      }

      setIsServiceTypeModalOpen(false);
      
      // Only refresh the specific data that changed, not the entire page
      if (viewMode === 'dealership' && selectedDealership) {
        // Refresh dealership pricing data
        await loadDealershipPricing();
        // Also refresh service types to get the newly created one
        const { data: serviceTypesData, error: serviceTypesError } = await serviceTypeService.getAllServiceTypes();
        if (!serviceTypesError) {
          setServiceTypes(serviceTypesData || []);
        }
      } else {
        // For global view, only refresh service types
        const { data: serviceTypesData, error: serviceTypesError } = await serviceTypeService.getAllServiceTypes();
        if (!serviceTypesError) {
          setServiceTypes(serviceTypesData || []);
        }
      }
      
      // Show success message
      alert(viewMode === 'dealership' ? 'Service erfolgreich erstellt!' : 'Service-Typ erfolgreich erstellt!');
    } catch (err) {
      console.error('Error saving service type:', err);
      alert('Fehler beim Speichern: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Pricing Modal handlers
  const handleEditPricing = (pricingItem) => {
    setEditingPricing(pricingItem);
    setPricingFormData({
      price: pricingItem.final_price || pricingItem.default_price || 0,
      is_active: pricingItem.pricing_active !== undefined ? pricingItem.pricing_active : true,
      service_name: pricingItem.service_type_display_name || ''
    });
    setPricingFormErrors({});
    setIsPricingModalOpen(true);
  };

  const handleSavePricing = async () => {
    // Basic validation
    if (!pricingFormData.price || pricingFormData.price < 0) {
      setPricingFormErrors({ price: 'Preis muss größer als 0 sein' });
      return;
    }

    if (!pricingFormData.service_name || pricingFormData.service_name.trim() === '') {
      setPricingFormErrors({ service_name: 'Service Name ist erforderlich' });
      return;
    }

    setIsSaving(true);
    try {
      // First update the service type display name if it changed
      if (pricingFormData.service_name !== editingPricing.service_type_display_name) {
        const { error: serviceError } = await serviceTypeService.updateServiceType(
          editingPricing.service_type_id,
          { display_name: pricingFormData.service_name }
        );
        if (serviceError) throw serviceError;
      }

      // Then update the pricing
      const { error } = await dealershipPricingService.upsertDealershipPricing(
        editingPricing.dealership_id,
        editingPricing.service_type_id,
        {
          price: pricingFormData.price,
          is_active: pricingFormData.is_active
        }
      );
      
      if (error) throw error;

      setIsPricingModalOpen(false);
      
      // Only refresh the specific data that changed, not the entire page
      if (viewMode === 'dealership' && selectedDealership) {
        // Refresh dealership pricing data
        await loadDealershipPricing();
        // Also refresh service types to get updated names
        const { data: serviceTypesData, error: serviceTypesError } = await serviceTypeService.getAllServiceTypes();
        if (!serviceTypesError) {
          setServiceTypes(serviceTypesData || []);
        }
      }
    } catch (err) {
      console.error('Error saving pricing:', err);
      alert('Fehler beim Speichern der Preise: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPricing = async () => {
    if (!selectedDealership) return;
    
    if (!window.confirm(`Möchten Sie alle Preise für "${selectedDealership.name}" auf die Standardpreise zurücksetzen?`)) {
      return;
    }

    try {
      const { error } = await dealershipPricingService.resetDealershipPricingToDefault(selectedDealership.id);
      if (error) throw error;
      
      await loadDealershipPricing();
      alert('Preise wurden erfolgreich auf Standardpreise zurückgesetzt.');
    } catch (err) {
      console.error('Error resetting pricing:', err);
      alert('Fehler beim Zurücksetzen der Preise: ' + err.message);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePricingInputChange = (field, value) => {
    setPricingFormData(prev => ({ ...prev, [field]: value }));
    if (pricingFormErrors[field]) {
      setPricingFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header userRole="admin" />
      
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
        />
      </div>

      {/* Main Content */}
      <main className={`pt-16 transition-all duration-200 ${
        isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
      }`}>
        <div className="p-4 lg:p-6">
          {/* Breadcrumb */}
          <Breadcrumb />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Service-Typen & Preismanagement
              </h1>
              <p className="text-muted-foreground">
                Verwalten Sie Service-Typen und dealership-spezifische Preise
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* <Button
                variant="outline"
                onClick={() => setViewMode(viewMode === 'dealership' ? 'global' : 'dealership')}
              >
                <Icon name={viewMode === 'dealership' ? 'Settings' : 'Building'} size={16} className="mr-2" />
                {viewMode === 'dealership' ? 'Globale Ansicht' : 'Dealership Ansicht'}
              </Button> */}
              {viewMode === 'global' && (
                <Button
                  variant="default"
                  onClick={handleCreateServiceType}
                  disabled={isLoading}
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Neuer Service-Typ
                </Button>
              )}
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-error font-medium">Fehler beim Laden der Daten</p>
              </div>
              <p className="text-error/80 text-sm mt-1">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadInitialData}
                className="mt-2"
              >
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Erneut versuchen
              </Button>
            </div>
          )}

          {/* Dealership Selection (only in dealership view) */}
          {viewMode === 'dealership' && (
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Autohaus auswählen
                  </label>
                  <Select
                    options={dealerships.map(d => ({
                      value: d.id,
                      label: d.name,
                      description: `${d.city}, ${d.code}`
                    }))}
                    value={selectedDealership?.id}
                    onChange={(dealershipId) => {
                      const dealership = dealerships.find(d => d.id === dealershipId);
                      setSelectedDealership(dealership);
                    }}
                    placeholder="Autohaus auswählen..."
                    className="w-full"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  {selectedDealership && (
                    <>
                                             <Button
                         variant="default"
                         onClick={() => {
                           // Show service type selection modal for adding new service
                           setEditingServiceType(null);
                           setFormData(serviceTypeService.getDefaultServiceTypeData());
                           setFormErrors({});
                           setIsServiceTypeModalOpen(true);
                         }}
                         className="whitespace-nowrap"
                       >
                         <Icon name="Plus" size={16} className="mr-2" />
                         Service hinzufügen
                       </Button>
                      <Button
                        variant="outline"
                        onClick={handleResetPricing}
                        className="whitespace-nowrap"
                      >
                        <Icon name="RotateCcw" size={16} className="mr-2" />
                        Preise zurücksetzen
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

                     {/* Search Filter */}
           <div className="mb-6">
             <input
               type="text"
               placeholder={viewMode === 'dealership' ? 'Service-Typen oder Autohaus suchen...' : 'Service-Typen suchen...'}
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
             />
           </div>

          {/* Loading State */}
          {isLoading && (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                Daten werden geladen...
              </h3>
              <p className="text-muted-foreground">
                Bitte warten Sie einen Moment.
              </p>
            </div>
          )}

          {/* Data Table */}
          {!isLoading && (
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              {viewMode === 'dealership' ? (
                // Dealership pricing table
                <div className="overflow-x-auto">
                  <table className="w-full">
                                         <thead className="bg-muted/50 border-b border-border">
                       <tr>
                         <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                           Service
                         </th>
                         <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                           PREIS
                         </th>
                         <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                           Aktionen
                         </th>
                       </tr>
                     </thead>
                    <tbody className="divide-y divide-border">
                                             {filteredData.map((item) => (
                         <tr key={`${item.dealership_id}-${item.service_type_id}`} className="hover:bg-muted/30">
                           <td className="px-6 py-4 whitespace-nowrap">
                             <div className="flex items-center space-x-3">
                               {/* <div 
                                 className="w-8 h-8 rounded-lg flex items-center justify-center"
                                 style={{ backgroundColor: item.service_type_color + '20' }}
                               >
                                 <Icon 
                                   name={item.service_type_icon} 
                                   size={16} 
                                   style={{ color: item.service_type_color }}
                                 />
                               </div> */}
                               <div>
                                 <div className="font-medium text-foreground">
                                   {item.service_type_display_name}
                                 </div>
                               </div>
                             </div>
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap">
                             <span className="font-semibold text-foreground">
                               {item.final_price ? `${item.final_price.toFixed(2)} €` : '0.00 €'}
                             </span>
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap">
                             <Button
                               variant="ghost"
                               size="icon"
                               onClick={() => handleEditPricing(item)}
                               className="h-8 w-8"
                             >
                               <Icon name="Edit" size={16} />
                             </Button>
                           </td>
                         </tr>
                       ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Global service types table
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Service
                        </th>
                        
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Standardpreis
                        </th>
                        
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredData.map((item) => (
                        <tr key={item.id} className="hover:bg-muted/30">
                                                     <td className="px-6 py-4 whitespace-nowrap">
                             <div className="flex items-center space-x-3">
                               <div>
                                 <div className="font-medium text-foreground">
                                   {item.display_name}
                                 </div>
                                 <div className="text-sm text-muted-foreground">
                                   {item.name}
                                 </div>
                                 {item.description && (
                                   <div className="text-xs text-muted-foreground mt-1 max-w-xs truncate">
                                     {item.description}
                                   </div>
                                 )}
                               </div>
                             </div>
                           </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-semibold text-foreground">
                              {item.price ? `${item.price.toFixed(2)} €` : '0.00 €'}
                            </span>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditServiceType(item)}
                              className="h-8 w-8"
                            >
                              <Icon name="Edit" size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Empty table state */}
              {filteredData.length === 0 && (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Settings" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {viewMode === 'dealership' 
                      ? 'Keine Preisdaten gefunden'
                      : 'Keine Service-Typen gefunden'
                    }
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {searchTerm
                      ? 'Versuchen Sie, Ihre Filter anzupassen.'
                      : viewMode === 'dealership'
                        ? 'Wählen Sie ein Autohaus aus, um die Preise zu verwalten.'
                        : 'Erstellen Sie Ihren ersten Service-Typ, um zu beginnen.'
                    }
                  </p>
                  {!searchTerm && viewMode === 'global' && (
                    <Button
                      variant="default"
                      onClick={handleCreateServiceType}
                    >
                      <Icon name="Plus" size={16} className="mr-2" />
                      Ersten Service-Typ erstellen
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredData.length === 0 && (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Settings" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                {viewMode === 'dealership' 
                  ? 'Keine Preisdaten gefunden'
                  : 'Keine Service-Typen gefunden'
                }
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm
                  ? 'Versuchen Sie, Ihre Filter anzupassen.'
                  : viewMode === 'dealership'
                    ? 'Wählen Sie ein Autohaus aus, um die Preise zu verwalten.'
                    : 'Erstellen Sie Ihren ersten Service-Typ, um zu beginnen.'
                }
              </p>
              {!searchTerm && viewMode === 'global' && (
                <Button
                  variant="default"
                  onClick={handleCreateServiceType}
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Ersten Service-Typ erstellen
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Quick Action Button */}
      <QuickActionButton userRole="admin" />

      {/* Service Type Modal */}
      {isServiceTypeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1200 p-4">
          <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Settings" size={20} color="white" />
                </div>
                                 <div>
                   <h2 className="text-xl font-semibold text-foreground">
                     {editingServiceType 
                       ? 'Service-Typ bearbeiten' 
                       : viewMode === 'dealership' 
                         ? 'Neuen Service für Autohaus hinzufügen'
                         : 'Neuer Service-Typ'
                     }
                   </h2>
                   {!editingServiceType && viewMode === 'dealership' && (
                     <p className="text-sm text-muted-foreground mt-1">
                       Der Service wird sowohl als Service-Typ als auch mit Preisen für "{selectedDealership?.name}" erstellt.
                     </p>
                   )}
                 </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsServiceTypeModalOpen(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {editingServiceType ? (
                // Full form for editing existing service type
                                 <div className="space-y-4">
                  
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Name (Technisch) *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                          formErrors.name ? 'border-error' : 'border-border'
                        }`}
                        placeholder="z.B. tango_wachs"
                        disabled={true}
                      />
                      {formErrors.name && (
                        <p className="text-error text-sm mt-1">{formErrors.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Anzeigename *
                      </label>
                      <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => handleInputChange('display_name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                          formErrors.display_name ? 'border-error' : 'border-border'
                        }`}
                        placeholder="z.B. Tango Wachs"
                      />
                      {formErrors.display_name && (
                        <p className="text-error text-sm mt-1">{formErrors.display_name}</p>
                      )}
                    </div>

                    

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Standardpreis (EUR)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                        className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                          formErrors.price ? 'border-error' : 'border-border'
                        }`}
                        placeholder="0.00"
                      />
                      {formErrors.price && (
                        <p className="text-error text-sm mt-1">{formErrors.price}</p>
                      )}
                    </div>
                </div>
              ) : (
                // Simple form for adding new service (dealership view)
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => handleInputChange('display_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                        formErrors.display_name ? 'border-error' : 'border-border'
                      }`}
                      placeholder="z.B. Tango Wachs"
                    />
                    {formErrors.display_name && (
                      <p className="text-error text-sm mt-1">{formErrors.display_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Preis (EUR) *
                    </label>
                    <input
                      type="text"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                        formErrors.price ? 'border-error' : 'border-border'
                      }`}
                      placeholder="0.00"
                    />
                    {formErrors.price && (
                      <p className="text-error text-sm mt-1">{formErrors.price}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Description - only show when editing */}
              {editingServiceType && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Beschreibung
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg bg-background text-foreground resize-none"
                    placeholder="Optionale Beschreibung des Service-Typs..."
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setIsServiceTypeModalOpen(false)}
              >
                Abbrechen
              </Button>
                             <Button
                 variant="default"
                 onClick={handleSaveServiceType}
                 loading={isSaving}
               >
                 <Icon name="Save" size={16} className="mr-2" />
                 {editingServiceType 
                   ? 'Speichern' 
                   : viewMode === 'dealership' 
                     ? 'Service & Preise erstellen'
                     : 'Erstellen'
                 }
               </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {isPricingModalOpen && editingPricing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1200 p-4">
          <div className="bg-card rounded-lg border border-border w-full max-w-md max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Euro" size={20} color="white" />
                </div>
                                 <div>
                   <h2 className="text-xl font-semibold text-foreground">
                     Service bearbeiten
                   </h2>
                   <p className="text-sm text-muted-foreground">
                     Name und Preis des Services anpassen
                   </p>
                 </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsPricingModalOpen(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={pricingFormData.service_name || editingPricing.service_type_display_name || ''}
                    onChange={(e) => handlePricingInputChange('service_name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                      pricingFormErrors.service_name ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Service Name eingeben"
                  />
                  {pricingFormErrors.service_name && (
                    <p className="text-error text-sm mt-1">{pricingFormErrors.service_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Preis (EUR) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingFormData.price}
                    onChange={(e) => handlePricingInputChange('price', parseFloat(e.target.value) || 0)}
                    className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                      pricingFormErrors.price ? 'border-error' : 'border-border'
                    }`}
                    placeholder="0.00"
                  />
                  {pricingFormErrors.price && (
                    <p className="text-error text-sm mt-1">{pricingFormErrors.price}</p>
                  )}
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setIsPricingModalOpen(false)}
              >
                Abbrechen
              </Button>
                             <Button
                 variant="default"
                 onClick={handleSavePricing}
                 loading={isSaving}
               >
                 <Icon name="Save" size={16} className="mr-2" />
                 Service & Preis speichern
               </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceTypesManagement;
